import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/AppShell";
import { EMPLOYEE_NAV } from "./_nav";
import { api, errFrom } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { DAYS, defaultWeekday, formatWeekLabel, currentMonday, shiftWeek } from "../../lib/schedule";
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Clock, Edit3, PlusCircle, Trash2 } from "lucide-react";

export default function EmployeeSchedule() {
  const [byWeek, setByWeek] = useState({});
  const [viewWeek, setViewWeek] = useState(currentMonday());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [isChangeRequest, setIsChangeRequest] = useState(false);
  const [availability, setAvailability] = useState(defaultWeekday());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/employee/schedules", { params: { weeks: 12 } });
      setByWeek(data.by_week || {});
    } catch (e) { toast.error(errFrom(e)); }
  }
  useEffect(() => { load(); }, []);

  const thisWeek = useMemo(() => currentMonday(), []);
  const isPastWeek = viewWeek < thisWeek;

  function openDialog(week, change, prefill) {
    setSelectedWeek(week);
    setIsChangeRequest(!!change);
    if (prefill) {
      const base = defaultWeekday();
      DAYS.forEach((d) => {
        const v = prefill[d.key];
        base[d.key] = v ? { available: !!v.available, start: v.start || "09:00", end: v.end || "17:00" } : { available: false, start: "09:00", end: "17:00" };
      });
      setAvailability(base);
    } else {
      setAvailability(defaultWeekday());
    }
    setNote("");
    setDialogOpen(true);
  }

  async function submit() {
    const payload = { ...availability };
    for (const d of DAYS) {
      const v = payload[d.key];
      if (!v.available) payload[d.key] = { available: false };
      else {
        if (!v.start || !v.end) return toast.error(`${d.label}: set start and end time`);
        if (v.start >= v.end) return toast.error(`${d.label}: end must be after start`);
      }
    }
    setLoading(true);
    try {
      await api.post("/employee/schedules", {
        week_start: selectedWeek,
        availability: payload,
        note: note || undefined,
      });
      toast.success(isChangeRequest ? "Change request submitted — pending approval" : "Schedule submitted — pending approval");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(errFrom(e)); }
    finally { setLoading(false); }
  }

  async function cancel(id) {
    try { await api.delete(`/employee/schedules/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(errFrom(e)); }
  }

  const records = (byWeek[viewWeek] || []).slice().sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  const approved = records.find((r) => r.status === "approved");
  const pendingNew = records.find((r) => r.status === "pending" && !r.is_change_request);
  const pendingChange = records.find((r) => r.status === "pending" && r.is_change_request);
  const rejected = records.filter((r) => r.status === "rejected").slice(0, 2);
  const primary = approved || pendingNew || records[0];

  return (
    <AppShell nav={EMPLOYEE_NAV} title="My Schedule" subtitle="Weekly availability">
      <div className="brand-surface rounded-2xl p-6 mb-6 bg-gradient-to-br from-[#141414] to-[#1C1C1E]" data-testid="schedule-intro">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#FF3B30]/15 text-[#FF3B30] flex items-center justify-center"><CalendarDays size={20} /></div>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-xl">Submit your weekly availability</h2>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">Let admin know which days and hours you can work each week. Your submission goes to pending approval. If your week is already approved and you need to swap a day, submit a change request.</p>
          </div>
        </div>
      </div>

      {/* Week navigator */}
      <div className="brand-surface rounded-2xl p-4 sm:p-5 mb-4 flex items-center justify-between gap-3" data-testid="week-navigator">
        <Button
          variant="ghost"
          onClick={() => setViewWeek(shiftWeek(viewWeek, -1))}
          className="text-zinc-300 hover:text-white hover:bg-white/5 h-11 px-3"
          data-testid="week-prev"
        >
          <ChevronLeft size={18} />
          <span className="hidden sm:inline ml-1">Prev</span>
        </Button>
        <div className="text-center flex-1 min-w-0">
          <div className="text-overline">Week of</div>
          <div className="font-heading font-bold text-lg sm:text-xl truncate" data-testid="week-label">{formatWeekLabel(viewWeek)}</div>
          {viewWeek === thisWeek && <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-semibold mt-0.5">Current week</div>}
        </div>
        <div className="flex items-center gap-1">
          {viewWeek !== thisWeek && (
            <Button
              variant="ghost"
              onClick={() => setViewWeek(thisWeek)}
              className="text-zinc-400 hover:text-white hover:bg-white/5 h-11 px-3 text-xs"
              data-testid="week-today"
            >
              Today
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => setViewWeek(shiftWeek(viewWeek, 1))}
            className="text-zinc-300 hover:text-white hover:bg-white/5 h-11 px-3"
            data-testid="week-next"
          >
            <span className="hidden sm:inline mr-1">Next</span>
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      {/* Week card */}
      <div className="brand-surface rounded-2xl p-5" data-testid={`schedule-week-${viewWeek}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-zinc-400">Availability status</div>
          <StatusBadge record={primary} />
        </div>

        {primary ? (
          <AvailabilityGrid availability={primary.availability} />
        ) : (
          <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-white/10 rounded-lg">No availability submitted for this week yet.</div>
        )}

        {primary?.note && (
          <div className="mt-3 text-xs text-zinc-500 italic">Your note: "{primary.note}"</div>
        )}
        {primary?.admin_note && (
          <div className="mt-2 text-xs"><span className="text-[#FF3B30] font-semibold">Admin:</span> <span className="text-zinc-300">{primary.admin_note}</span></div>
        )}

        {pendingChange && (
          <div className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold mb-1">Change request pending</div>
            <AvailabilityGrid availability={pendingChange.availability} compact />
            {pendingChange.note && <div className="text-[11px] text-zinc-500 italic mt-2">Your note: "{pendingChange.note}"</div>}
            <Button size="sm" variant="ghost" className="mt-2 text-xs text-zinc-400 hover:text-white h-7" onClick={() => cancel(pendingChange.id)} data-testid={`cancel-change-${viewWeek}`}><Trash2 size={12} className="mr-1" />Cancel request</Button>
          </div>
        )}

        {rejected.length > 0 && !approved && !pendingNew && (
          <div className="mt-3 text-[11px] text-red-400">Previously rejected {rejected.length}x. Update and resubmit.</div>
        )}

        {isPastWeek ? (
          <div className="mt-4 text-xs text-zinc-500 italic text-center py-2">This week has ended — schedules for past weeks cannot be modified.</div>
        ) : (
          <div className="mt-4 flex gap-2">
            {!approved && !pendingNew && (
              <Button onClick={() => openDialog(viewWeek, false, primary?.availability)} className="bg-[#FF3B30] hover:bg-[#E02920] h-10 flex-1" data-testid={`submit-availability-${viewWeek}`}>
                <PlusCircle size={14} className="mr-2" />Submit availability
              </Button>
            )}
            {pendingNew && (
              <>
                <Button onClick={() => openDialog(viewWeek, false, pendingNew.availability)} variant="secondary" className="bg-[#1C1C1E] border border-white/10 text-white hover:bg-[#2C2C2E] h-10 flex-1" data-testid={`edit-pending-${viewWeek}`}><Edit3 size={14} className="mr-2" />Update pending</Button>
                <Button onClick={() => cancel(pendingNew.id)} variant="ghost" className="text-zinc-400 hover:text-red-400 h-10" data-testid={`cancel-pending-${viewWeek}`}><Trash2 size={14} /></Button>
              </>
            )}
            {approved && !pendingChange && (
              <Button onClick={() => openDialog(viewWeek, true, approved.availability)} variant="secondary" className="bg-[#1C1C1E] border border-white/10 text-white hover:bg-[#2C2C2E] h-10 flex-1" data-testid={`request-change-${viewWeek}`}><Edit3 size={14} className="mr-2" />Request change</Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white max-w-2xl" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{isChangeRequest ? "Request schedule change" : "Submit availability"}</DialogTitle>
            <div className="text-sm text-zinc-400">Week of {selectedWeek && formatWeekLabel(selectedWeek)}</div>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto pr-2">
            {DAYS.map((d) => {
              const v = availability[d.key];
              return (
                <div key={d.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${v.available ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : "border-white/10"}`} data-testid={`day-row-${d.key}`}>
                  <Checkbox
                    id={`av-${d.key}`} checked={v.available}
                    onCheckedChange={(c) => setAvailability({ ...availability, [d.key]: { ...v, available: !!c } })}
                    className="border-white/30 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
                    data-testid={`day-checkbox-${d.key}`}
                  />
                  <label htmlFor={`av-${d.key}`} className="w-24 font-semibold cursor-pointer">{d.label}</label>
                  {v.available ? (
                    <div className="flex items-center gap-2 ml-auto">
                      <Input type="time" value={v.start} onChange={(e) => setAvailability({ ...availability, [d.key]: { ...v, start: e.target.value } })} className="h-9 bg-[#0A0A0A] border-white/10 w-28" data-testid={`day-start-${d.key}`} />
                      <span className="text-zinc-500 text-xs">to</span>
                      <Input type="time" value={v.end} onChange={(e) => setAvailability({ ...availability, [d.key]: { ...v, end: e.target.value } })} className="h-9 bg-[#0A0A0A] border-white/10 w-28" data-testid={`day-end-${d.key}`} />
                    </div>
                  ) : (
                    <span className="ml-auto text-xs text-zinc-500 uppercase tracking-wider">Off</span>
                  )}
                </div>
              );
            })}
            <div className="pt-2">
              <Label className="text-overline">Note to admin (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 bg-[#0A0A0A] border-white/10" placeholder={isChangeRequest ? "Why you need this change…" : "Any preferences…"} data-testid="schedule-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={submit} disabled={loading} className="bg-[#FF3B30] hover:bg-[#E02920]" data-testid="schedule-submit-button">
              <CheckCircle2 size={14} className="mr-2" />{loading ? "Submitting…" : (isChangeRequest ? "Submit change request" : "Submit availability")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function StatusBadge({ record }) {
  if (!record) return <Badge className="bg-zinc-500/15 text-zinc-400 border-zinc-500/30 border text-[10px] uppercase tracking-widest">None</Badge>;
  const map = {
    pending: ["bg-amber-500/15 text-amber-400 border-amber-500/30", record.is_change_request ? "Change · pending" : "Pending"],
    approved: ["bg-green-500/15 text-green-400 border-green-500/30", "Approved"],
    rejected: ["bg-red-500/15 text-red-400 border-red-500/30", "Rejected"],
    superseded: ["bg-zinc-500/15 text-zinc-400 border-zinc-500/30", "Superseded"],
  };
  const [cls, label] = map[record.status] || ["bg-zinc-500/15 text-zinc-400 border-zinc-500/30", record.status];
  return <Badge className={`${cls} border text-[10px] uppercase tracking-widest font-semibold`}>{label}</Badge>;
}

export function AvailabilityGrid({ availability, compact = false }) {
  return (
    <div className="-mx-1 overflow-x-auto">
      <div className={`grid grid-cols-7 gap-1 min-w-[420px] px-1 ${compact ? "text-[10px]" : "text-xs"}`}>
        {DAYS.map((d) => {
          const v = (availability || {})[d.key];
          const on = v?.available;
          return (
            <div key={d.key} className={`rounded-md ${compact ? "p-1.5" : "p-2"} text-center border ${on ? "border-[#FF3B30]/40 bg-[#FF3B30]/10" : "border-white/5 bg-[#0A0A0A]"}`}>
              <div className="uppercase tracking-wider text-[9px] text-zinc-500">{d.key}</div>
              {on ? (
                <div className="font-mono text-white mt-0.5 leading-tight"><Clock size={9} className="inline mr-0.5 text-[#FF3B30]" />{v.start}<br />{v.end}</div>
              ) : (
                <div className="text-zinc-600 mt-1">Off</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
